<template>
  <section class="dz">
    <div class="dz__head">
      <div>
        <h2><CygnusIcon name="envelope" /> Day zero mail</h2>
        <p class="text-muted">
          Post a welcome mail piece to a company the day it appears on the register. When a brand-new
          company matches your targeting, it joins a rolling daily batch automatically - metered per piece,
          one piece per company. Switch a rule on to start; caps keep your spend predictable.
        </p>
      </div>
      <button v-if="!showForm && rules.length" class="btn dz-btn-primary" @click="openCreate">
        <CygnusIcon name="plus" /> New rule
      </button>
    </div>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
    <div v-if="message" class="alert alert-success py-2 small">{{ message }}</div>

    <div v-if="loading" class="dz-loading">Loading day zero mail rules...</div>

    <!-- Empty state -->
    <div v-else-if="!rules.length && !showForm" class="dz-empty">
      <div class="dz-empty__icon"><CygnusIcon name="envelope" /></div>
      <div class="dz-empty__title">Reach new companies with a welcome mailer</div>
      <div class="dz-empty__hint">
        Create a rule, pick a mail-piece design, set your daily and monthly caps, then switch it on. New
        companies that match your targeting will be mailed once, automatically, on day one.
      </div>
      <button class="btn dz-btn-primary" @click="openCreate"><CygnusIcon name="plus" /> Create a rule</button>
    </div>

    <!-- Rule cards -->
    <div v-else-if="!showForm" class="dz-rules">
      <div v-for="r in rules" :key="r.id" class="dz-rule" :class="{ 'is-active': r.active }">
        <div class="dz-rule__main">
          <div class="dz-rule__top">
            <span class="dz-rule__name">{{ r.name }}</span>
            <span class="dz-state" :class="r.active ? 'on' : 'off'">
              <span class="dot"></span>{{ r.active ? 'Active' : 'Off' }}
            </span>
          </div>
          <div class="dz-rule__meta">
            <span class="dz-chip">
              <CygnusIcon name="palette" />
              {{ r.design_name || 'No design' }}
              <span v-if="r.design_ready" class="ready">ready</span>
              <span v-else class="draft">draft</span>
            </span>
            <span class="dz-chip" :class="{ capped: r.daily_capped }">
              <CygnusIcon name="clock" /> {{ r.daily_used }} / {{ r.daily_cap }} today
            </span>
            <span class="dz-chip" :class="{ capped: r.monthly_capped }">
              <CygnusIcon name="calendar" /> {{ r.monthly_used }} / {{ r.monthly_cap }} this month
            </span>
            <span class="dz-chip subtle"><CygnusIcon name="check" /> {{ r.total_sent }} sent</span>
            <span v-if="!r.use_fit_criteria" class="dz-chip subtle">Filters only</span>
          </div>
          <div v-if="(r.daily_capped || r.monthly_capped) && r.active" class="dz-capnote">
            <CygnusIcon name="info-circle" />
            {{ r.daily_capped ? 'Daily cap reached' : 'Monthly cap reached' }} - new matches wait until the cap resets.
          </div>
        </div>
        <div class="dz-rule__actions">
          <label class="dz-toggle" :title="r.active ? 'Switch off' : 'Switch on'">
            <input type="checkbox" :checked="r.active" :disabled="busy" @change="onToggle(r, $event)">
            <span class="dz-toggle__track"><span class="dz-toggle__thumb"></span></span>
          </label>
          <button class="btn dz-btn-ghost btn-sm" :disabled="busy" @click="openEdit(r)"><CygnusIcon name="gear" /> Edit</button>
          <button class="btn dz-btn-ghost btn-sm danger" :disabled="busy" @click="onDelete(r)"><CygnusIcon name="trash" /></button>
        </div>
      </div>
    </div>

    <!-- Create / edit form -->
    <div v-else class="dz-form">
      <h3>{{ form.id ? 'Edit rule' : 'New day zero mail rule' }}</h3>
      <div class="dz-field">
        <label>Rule name</label>
        <input v-model="form.name" class="dz-input" maxlength="190" placeholder="e.g. New accountancy firms - welcome">
      </div>
      <div class="dz-field">
        <label>Mail-piece design</label>
        <select v-model="form.design_id" class="dz-input">
          <option :value="0" disabled>Choose a design...</option>
          <option v-for="d in designs" :key="d.id" :value="d.id">
            {{ d.name }}{{ d.status === 'ready' ? '' : ' (draft)' }}
          </option>
        </select>
        <p class="dz-hint">Only a design marked ready can be switched on. Build designs in the Direct Mail builder.</p>
      </div>
      <div class="dz-grid2">
        <div class="dz-field">
          <label>Daily cap</label>
          <input v-model.number="form.daily_cap" type="number" min="0" max="100000" class="dz-input">
        </div>
        <div class="dz-field">
          <label>Monthly cap</label>
          <input v-model.number="form.monthly_cap" type="number" min="0" max="100000" class="dz-input">
        </div>
      </div>
      <div class="dz-field">
        <label class="dz-check">
          <input type="checkbox" v-model="form.use_fit_criteria">
          Only mail companies that match my targeting (fit criteria)
        </label>
        <p class="dz-hint">On (recommended): the same targeting that surfaces your Companies House signals. Off: rely solely on the narrowing filters below.</p>
      </div>
      <details class="dz-narrow">
        <summary>Optional narrowing</summary>
        <div class="dz-field">
          <label>Regions (postcode areas)</label>
          <input v-model="form.regionsText" class="dz-input" placeholder="e.g. OX, GL, SW">
          <p class="dz-hint">Comma-separated postcode-area codes. Leave blank for any region.</p>
        </div>
        <div class="dz-field">
          <label>SIC codes</label>
          <input v-model="form.sicText" class="dz-input" placeholder="e.g. 62012, 69201">
          <p class="dz-hint">Comma-separated SIC code prefixes. A company matches if any of its codes starts with one of these.</p>
        </div>
      </details>
      <div class="dz-form__actions">
        <button class="btn dz-btn-primary" :disabled="busy || !formValid" @click="save">
          <CygnusIcon name="check" /> {{ form.id ? 'Save changes' : 'Create rule' }}
        </button>
        <button class="btn dz-btn-ghost" :disabled="busy" @click="closeForm">Cancel</button>
      </div>
    </div>

    <!-- Activate confirm dialog -->
    <div v-if="confirmRule" class="dz-modal" @click.self="confirmRule = null">
      <div class="dz-modal__card">
        <h3><CygnusIcon name="envelope" /> Switch this rule on?</h3>
        <p>
          New companies matching your targeting will automatically receive this mail piece - metered per
          piece. You will be charged for each posted piece, up to your daily cap of
          <strong>{{ confirmRule.daily_cap }}</strong> and monthly cap of <strong>{{ confirmRule.monthly_cap }}</strong>.
        </p>
        <p class="text-muted small">Each company is mailed at most once per rule. You can switch this off at any time.</p>
        <div class="dz-modal__actions">
          <button class="btn dz-btn-primary" :disabled="busy" @click="confirmActivate">Switch on</button>
          <button class="btn dz-btn-ghost" :disabled="busy" @click="confirmRule = null">Cancel</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

/**
 * DayZeroMail — the tenant's day-zero welcome-mail rule manager (Direct Mail
 * Phase C). Lives on the Companies House feed page (the most discoverable home:
 * the user is already looking at their day-zero companies). A rule pairs a
 * mail-piece design with daily/monthly caps and an active toggle; switching it on
 * requires a paying plan (the API returns a 402-shape) and a ready design. Live
 * cap counters + a capped indicator come straight off the rule read model.
 */
export default {
  name: "DayZeroMail",
  data() {
    return {
      api: useApi(),
      loading: true,
      busy: false,
      error: null,
      message: "",
      rules: [],
      designs: [],
      defaults: { daily_cap: 25, monthly_cap: 300 },
      showForm: false,
      form: this.blankForm(),
      confirmRule: null,
    };
  },
  computed: {
    formValid() {
      return String(this.form.name || "").trim().length > 0 && Number(this.form.design_id) > 0;
    },
  },
  created() {
    this.load();
  },
  methods: {
    blankForm() {
      return { id: null, name: "", design_id: 0, use_fit_criteria: true, daily_cap: 25, monthly_cap: 300, regionsText: "", sicText: "" };
    },
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const [rr, dd] = await Promise.all([
          this.api.get("/api/v1/direct-mail/day-zero-rules"),
          this.api.get("/api/v1/direct-mail/designs"),
        ]);
        const rb = rr?.data ?? rr;
        this.rules = Array.isArray(rb?.rules) ? rb.rules : [];
        this.defaults = rb?.defaults || this.defaults;
        const db = dd?.data ?? dd;
        this.designs = Array.isArray(db?.designs) ? db.designs : [];
      } catch (e) {
        this.error = this.errText(e, "Could not load day zero mail rules.");
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.message = "";
      this.form = this.blankForm();
      this.form.daily_cap = this.defaults.daily_cap;
      this.form.monthly_cap = this.defaults.monthly_cap;
      this.showForm = true;
    },
    openEdit(r) {
      this.message = "";
      this.form = {
        id: r.id,
        name: r.name,
        design_id: r.design_id,
        use_fit_criteria: r.use_fit_criteria,
        daily_cap: r.daily_cap,
        monthly_cap: r.monthly_cap,
        regionsText: (r.filters?.regions || []).join(", "),
        sicText: (r.filters?.sic || []).join(", "),
      };
      this.showForm = true;
    },
    closeForm() {
      this.showForm = false;
      this.form = this.blankForm();
    },
    payload() {
      const list = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);
      return {
        name: this.form.name,
        design_id: Number(this.form.design_id),
        use_fit_criteria: !!this.form.use_fit_criteria,
        daily_cap: Number(this.form.daily_cap),
        monthly_cap: Number(this.form.monthly_cap),
        filters: { regions: list(this.form.regionsText), sic: list(this.form.sicText) },
      };
    },
    async save() {
      this.busy = true;
      this.error = null;
      try {
        if (this.form.id) {
          const res = await this.api.patch("/api/v1/direct-mail/day-zero-rules/" + this.form.id, this.payload());
          this.applyRule((res?.data ?? res)?.rule);
          this.message = "Rule saved.";
        } else {
          const res = await this.api.post("/api/v1/direct-mail/day-zero-rules", this.payload());
          const rule = (res?.data ?? res)?.rule;
          if (rule) this.rules.unshift(rule);
          this.message = "Rule created. Switch it on when you are ready to start mailing.";
        }
        this.closeForm();
      } catch (e) {
        this.error = this.errText(e, "Could not save the rule.");
      } finally {
        this.busy = false;
      }
    },
    onToggle(r, ev) {
      const turningOn = ev.target.checked;
      // Revert the visual toggle until the action resolves (confirm / API).
      ev.target.checked = r.active;
      if (turningOn) {
        this.confirmRule = r;
      } else {
        this.patchActive(r, false);
      }
    },
    confirmActivate() {
      const r = this.confirmRule;
      this.confirmRule = null;
      if (r) this.patchActive(r, true);
    },
    async patchActive(r, active) {
      this.busy = true;
      this.error = null;
      this.message = "";
      try {
        const res = await this.api.patch("/api/v1/direct-mail/day-zero-rules/" + r.id, { active });
        this.applyRule((res?.data ?? res)?.rule);
        this.message = active ? "Rule switched on - new matching companies will be mailed." : "Rule switched off.";
      } catch (e) {
        this.error = this.errText(e, active ? "Could not switch the rule on." : "Could not switch the rule off.");
      } finally {
        this.busy = false;
      }
    },
    async onDelete(r) {
      if (!window.confirm("Delete this day zero mail rule? Companies already mailed are unaffected.")) return;
      this.busy = true;
      this.error = null;
      try {
        await this.api.delete("/api/v1/direct-mail/day-zero-rules/" + r.id);
        this.rules = this.rules.filter((x) => x.id !== r.id);
        this.message = "Rule deleted.";
      } catch (e) {
        this.error = this.errText(e, "Could not delete the rule.");
      } finally {
        this.busy = false;
      }
    },
    applyRule(rule) {
      if (!rule) return;
      const i = this.rules.findIndex((x) => x.id === rule.id);
      if (i >= 0) this.rules.splice(i, 1, rule);
      else this.rules.unshift(rule);
    },
    errText(e, fallback) {
      const body = e?.response?.data ?? e?.data;
      return body?.error || body?.message || fallback;
    },
  },
};
</script>

<style scoped>
.dz { margin: 26px 0 6px; padding-top: 22px; border-top: 1px solid var(--m-border); }
.dz__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.dz__head h2 { font-size: 1.2rem; font-weight: 700; margin: 0 0 6px; display: flex; align-items: center; gap: 9px; }
.dz__head h2 i { color: var(--m-teal, #14B8A6); }
.dz__head p { margin: 0; max-width: 720px; font-size: 0.86rem; line-height: 1.55; }
.text-muted { color: var(--m-text-secondary); }

.dz-loading { padding: 24px; text-align: center; color: var(--m-text-secondary); }

.dz-empty { text-align: center; padding: 34px 24px; background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg, 14px); margin-top: 16px; }
.dz-empty__icon { display: inline-flex; align-items: center; justify-content: center; width: 56px; height: 56px; border-radius: 50%; background: var(--m-teal-soft, #E4F7F4); color: var(--m-teal-text, #0F766E); font-size: 22px; margin-bottom: 14px; }
.dz-empty__title { font-weight: 600; color: var(--m-text-primary); font-size: 1rem; margin-bottom: 8px; }
.dz-empty__hint { font-size: 0.85rem; max-width: 520px; margin: 0 auto 18px; line-height: 1.6; color: var(--m-text-secondary); }

.dz-rules { display: grid; gap: 12px; margin-top: 16px; }
.dz-rule { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg, 14px); padding: 15px 17px; }
.dz-rule.is-active { border-color: var(--m-teal-line, #B6EBE4); box-shadow: 0 0 0 1px var(--m-teal-line, #B6EBE4) inset; }
.dz-rule__main { min-width: 0; flex: 1; }
.dz-rule__top { display: flex; align-items: center; gap: 10px; margin-bottom: 9px; }
.dz-rule__name { font-weight: 600; font-size: 0.96rem; color: var(--m-text-primary); }
.dz-state { display: inline-flex; align-items: center; gap: 5px; font-size: 0.66rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; border-radius: 999px; padding: 2px 8px; }
.dz-state.on { background: var(--m-teal-soft, #E4F7F4); color: var(--m-teal-text, #0F766E); }
.dz-state.off { background: #F1F3F7; color: #697086; }
.dz-state .dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.dz-rule__meta { display: flex; flex-wrap: wrap; gap: 8px; }
.dz-chip { display: inline-flex; align-items: center; gap: 6px; font-size: 0.76rem; font-weight: 600; background: #EEF1F6; color: #556074; border-radius: 6px; padding: 3px 9px; }
.dz-chip i { font-size: 0.72rem; }
.dz-chip.subtle { background: transparent; color: var(--m-text-tertiary); }
.dz-chip.capped { background: #FFF4E6; color: #9A5B12; }
.dz-chip .ready { font-size: 0.6rem; font-weight: 800; text-transform: uppercase; color: var(--m-teal-text, #0F766E); }
.dz-chip .draft { font-size: 0.6rem; font-weight: 800; text-transform: uppercase; color: #9A5B12; }
.dz-capnote { display: flex; align-items: center; gap: 7px; margin-top: 10px; font-size: 0.78rem; color: #9A5B12; }
.dz-rule__actions { display: flex; align-items: center; gap: 8px; flex: none; }

.dz-toggle { position: relative; display: inline-flex; cursor: pointer; }
.dz-toggle input { position: absolute; opacity: 0; width: 0; height: 0; }
.dz-toggle__track { width: 38px; height: 22px; border-radius: 999px; background: #CBD2DE; transition: background 0.15s; display: inline-block; position: relative; }
.dz-toggle__thumb { position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: transform 0.15s; box-shadow: 0 1px 2px rgba(0,0,0,0.2); }
.dz-toggle input:checked + .dz-toggle__track { background: var(--m-teal, #14B8A6); }
.dz-toggle input:checked + .dz-toggle__track .dz-toggle__thumb { transform: translateX(16px); }

.dz-form { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg, 14px); padding: 20px 22px; margin-top: 16px; max-width: 640px; }
.dz-form h3 { margin: 0 0 16px; font-size: 1.02rem; font-weight: 700; }
.dz-field { margin-bottom: 14px; }
.dz-field > label { display: block; font-size: 0.78rem; font-weight: 600; color: var(--m-text-secondary); margin-bottom: 5px; }
.dz-input { width: 100%; border: 1px solid var(--m-border); background: var(--m-bg-input, var(--m-bg-card)); border-radius: 7px; font-size: 0.86rem; padding: 8px 11px; color: var(--m-text-primary); font-family: inherit; }
.dz-hint { font-size: 0.74rem; color: var(--m-text-tertiary); margin: 5px 0 0; line-height: 1.45; }
.dz-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.dz-check { display: flex; align-items: flex-start; gap: 8px; font-size: 0.84rem; font-weight: 500; color: var(--m-text-primary); cursor: pointer; }
.dz-check input { margin-top: 2px; }
.dz-narrow { margin: 4px 0 16px; border: 1px solid var(--m-border); border-radius: 8px; padding: 6px 12px; }
.dz-narrow summary { cursor: pointer; font-size: 0.8rem; font-weight: 600; color: var(--m-text-secondary); padding: 6px 0; }
.dz-narrow[open] summary { margin-bottom: 8px; }
.dz-form__actions { display: flex; gap: 10px; margin-top: 6px; }

.dz-modal { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
.dz-modal__card { background: var(--m-bg-card); border-radius: 14px; padding: 24px 26px; max-width: 460px; box-shadow: var(--m-shadow-panel, 0 12px 40px rgba(0,0,0,0.2)); }
.dz-modal__card h3 { margin: 0 0 12px; font-size: 1.05rem; font-weight: 700; display: flex; align-items: center; gap: 8px; }
.dz-modal__card h3 i { color: var(--m-teal, #14B8A6); }
.dz-modal__card p { font-size: 0.86rem; line-height: 1.55; margin: 0 0 10px; }
.dz-modal__actions { display: flex; gap: 10px; margin-top: 16px; }

.btn { font-family: inherit; font-weight: 500; font-size: 0.82rem; padding: 0.45rem 0.9rem; border-radius: 7px; border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; line-height: 1.3; white-space: nowrap; }
.btn:disabled { opacity: 0.5; cursor: default; }
.btn-sm { font-size: 0.76rem; padding: 0.32rem 0.6rem; }
.dz-btn-primary { background: var(--m-teal, #14B8A6); border-color: var(--m-teal, #14B8A6); color: #fff; }
.dz-btn-primary:hover:not(:disabled) { background: var(--m-teal-text, #0F766E); border-color: var(--m-teal-text, #0F766E); }
.dz-btn-ghost { background: none; border-color: var(--m-border); color: var(--m-text-secondary); }
.dz-btn-ghost:hover:not(:disabled) { background: var(--m-rail-hover-bg, #F1F3F7); color: var(--m-text-primary); }
.dz-btn-ghost.danger { color: #B4232A; border-color: transparent; }
.dz-btn-ghost.danger:hover:not(:disabled) { background: #FDECEC; }

@media (max-width: 700px) {
  .dz-rule { flex-direction: column; }
  .dz-rule__actions { align-self: flex-end; }
  .dz-grid2 { grid-template-columns: 1fr; }
}
</style>
